import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react";
import { api, tokenStorage } from "@/lib/api";
import type { User } from "@/lib/auth";
import {
	storeUser,
	getStoredUser,
	clearStoredUser,
	isAuthenticated,
} from "@/lib/auth";

// =====================
// Types
// =====================

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (email: string, password: string, turnstileToken?: string) => Promise<LoginResult>;
	verifyMfa: (code: string) => Promise<void>;
	logout: () => void;
	updateUser: (user: User) => void;
}

interface LoginResponse {
	accessToken: string;
	user: User;
	mfaRequired?: boolean;
	mfaToken?: string;
}

interface LoginResult {
	mfaRequired: boolean;
	mfaToken?: string;
}

// =====================
// Context
// =====================

const AuthContext = createContext<AuthContextType | null>(null);

// =====================
// Provider
// =====================

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Check for existing auth on mount
	useEffect(() => {
		const checkAuth = async () => {
			const storedUser = getStoredUser();
			let hasToken = isAuthenticated();

			// H2: refresh token is httpOnly-cookie only. If the access token is
			// missing from localStorage, try one silent cookie refresh so a valid
			// session is restored on boot. Failure just means "not logged in" —
			// never clearTokens/redirect here (anonymous visitors hit this too).
			if (!hasToken && storedUser) {
				try {
					const res = await fetch("/api/auth/refresh", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
					});
					if (res.ok) {
						const data = await res.json();
						if (data.accessToken) {
							tokenStorage.setAccessToken(data.accessToken);
							hasToken = true;
						}
					}
				} catch {
					// network error — treat as logged out
				}
			}

			if (storedUser && hasToken) {
				// Verify token is still valid
				try {
					const response = await api.get<{ user: User }>("/auth/me");

					if (response.data?.user) {
						// Token is valid - use server data
						setUser(response.data.user);
						storeUser(response.data.user);
					} else if (response.status === 401) {
						// Token invalid/expired - clear auth immediately
						// (do NOT fall back to stored user)
						clearAuth();
					}
					// For other error statuses (500, etc.), fall through to catch
				} catch {
					// Network error or server unreachable - use stored user as fallback
					// This allows offline browsing with cached data
					setUser(storedUser);
				}
			}

			setIsLoading(false);
		};

		checkAuth();

		// Listen for token clearing events (triggered from any API 401)
		// On auth:cleared, we clear user state and redirect to login.
		// The event may fire multiple times if clearTokens() is called
		// in succession, but the redirect + state clear is idempotent.
		const handleAuthCleared = () => {
			console.warn('[AUTH:DEBUG] auth:cleared event received');
			console.trace('[AUTH:DEBUG] Stack trace for auth:cleared redirect');
			clearStoredUser();
			setUser(null);
			window.location.href = "/login";
		};
		window.addEventListener("auth:cleared", handleAuthCleared);
		return () => window.removeEventListener("auth:cleared", handleAuthCleared);
	}, []);

	// Login handler. Returns whether MFA is required to complete sign-in.
	const login = useCallback(async (email: string, password: string, turnstileToken?: string): Promise<LoginResult> => {
		setIsLoading(true);

		try {
			const response = await api.post<LoginResponse>("/auth/login", {
				email,
				password,
				turnstileToken,
			});

			if (response.error) {
				const err = new Error(response.error.message) as any;
				err.code = response.error.code;
				err.status = response.status;
				err.attemptsRemaining = (response.data as any)?.attemptsRemaining;
				err.details = response.error.details;
				err.fullData = response.data;
				throw err;
			}

			if (response.data) {
				const {
					accessToken,
					user: userData,
					mfaRequired,
					mfaToken,
				} = response.data;

				// MFA pending: store the short-lived token so /auth/mfa/verify
				// can authenticate; do NOT set the user yet.
				if (mfaRequired) {
					if (mfaToken) tokenStorage.setAccessToken(mfaToken);
					return { mfaRequired: true, mfaToken };
				}

				// Store access token; the refresh token is in the httpOnly cookie
				if (accessToken) tokenStorage.setAccessToken(accessToken);

				// Store user
				storeUser(userData);
				setUser(userData);
			}

			return { mfaRequired: false };
		} finally {
			setIsLoading(false);
		}
	}, []);

	// MFA verify handler. Completes sign-in after a TOTP/backup code check.
	const verifyMfa = useCallback(async (code: string) => {
		setIsLoading(true);

		try {
			const response = await api.post<LoginResponse>("/auth/mfa/verify", { code });

			if (response.error) {
				const err = new Error(response.error.message) as any;
				err.code = response.error.code;
				err.attemptsRemaining = (response.data as any)?.attemptsRemaining;
				err.details = response.error.details;
				err.fullData = response.data;
				throw err;
			}

			if (response.data) {
				const { accessToken, user: userData } = response.data;

				if (accessToken) tokenStorage.setAccessToken(accessToken);

				storeUser(userData);
				setUser(userData);
			}
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Logout handler
	const logout = useCallback(() => {
		console.warn('[AUTH:DEBUG] logout() called');
		console.trace('[AUTH:DEBUG] Stack trace for logout');
		clearAuth();
		window.location.href = "/login";
	}, []);

	// Update user data
	const updateUser = useCallback((updatedUser: User) => {
		storeUser(updatedUser);
		setUser(updatedUser);
	}, []);

	return (
		<AuthContext.Provider
			value={{
				user,
				isAuthenticated: !!user,
				isLoading,
				login,
				verifyMfa,
				logout,
				updateUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

// =====================
// Helper Functions
// =====================

function clearAuth() {
	console.warn('[AUTH:DEBUG] clearAuth() called');
	console.trace('[AUTH:DEBUG] Stack trace for clearAuth');
	tokenStorage.clearTokens();
	clearStoredUser();
	// Note: setUser(null) is called by the auth:cleared event handler
}

// =====================
// Hook
// =====================

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

// =====================
// Protected Route Component
// =====================

interface ProtectedRouteProps {
	children: ReactNode;
	allowedRoles?: User["role"][];
}

export function ProtectedRoute({
	children,
	allowedRoles,
}: ProtectedRouteProps) {
	const { user, isAuthenticated, isLoading } = useAuth();

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent" />
			</div>
		);
	}

	if (!isAuthenticated) {
		console.warn('[AUTH:DEBUG] ProtectedRoute: not authenticated, redirecting to /login');
		console.trace('[AUTH:DEBUG] Stack trace for ProtectedRoute redirect');
		window.location.href = "/login";
		return null;
	}

	if (allowedRoles && user && !allowedRoles.includes(user.role)) {
		console.warn('[AUTH:DEBUG] ProtectedRoute: wrong role, redirecting to /dashboard');
		console.trace('[AUTH:DEBUG] Stack trace for role redirect');
		window.location.href = "/dashboard";
		return null;
	}

	return <>{children}</>;
}
